import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, AlertCircle, AlertTriangle, Info, Check, X, RefreshCw, Clock, Inbox } from 'lucide-react';
import {
  listTarefas,
  refreshTarefasComputed,
  dismissTarefa,
  snoozeTarefa,
  Tarefa,
} from '../services/apiService';
import { MarkDoneModal } from '../components/MarkDoneModal';
import { NewTarefaModal } from '../components/NewTarefaModal';
import { Plus } from 'lucide-react';

type StatusFilter = 'open' | 'snoozed' | 'done' | 'dismissed';

const STATUS_LABELS: Record<StatusFilter, string> = {
  open: 'Abertas',
  snoozed: 'Adiadas',
  done: 'Concluídas',
  dismissed: 'Descartadas',
};

const SEVERITY_STYLES: Record<string, { icon: React.ReactNode; row: string; chip: string }> = {
  critical: {
    icon: <AlertCircle className="h-4 w-4 text-red-600" />,
    row: 'border-red-200',
    chip: 'bg-red-100 text-red-700',
  },
  warning: {
    icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    row: 'border-amber-200',
    chip: 'bg-amber-100 text-amber-700',
  },
  info: {
    icon: <Info className="h-4 w-4 text-blue-600" />,
    row: 'border-blue-200',
    chip: 'bg-blue-100 text-blue-700',
  },
};

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fmtTs(iso?: string): string {
  if (!iso) return '—';
  return iso.replace('T', ' ').replace('Z', '');
}

function daysDelta(iso: string): number {
  const today = new Date();
  const target = new Date(iso + 'T00:00:00');
  return Math.floor((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
}

export default function TarefasPage() {
  const [status, setStatus] = useState<StatusFilter>('open');
  const [severity, setSeverity] = useState<string>('');
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [doneModalTarefa, setDoneModalTarefa] = useState<Tarefa | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await listTarefas(status, severity || undefined);
      setTarefas(res.tarefas);
      setError('');
    } catch {
      setError('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, severity]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await refreshTarefasComputed();
      let msg = `${result.created} criadas, ${result.skipped} skip`;
      if (result.recreated) msg += `, ${result.recreated} recriadas`;
      const ar = (result as any).auto_resolved;
      if (ar) msg += `, ${ar} auto-resolvidas`;
      if (result.errors) msg += `, ${result.errors} erros`;
      alert(msg);
      await reload();
    } catch {
      setError('Erro ao atualizar');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDone = (tarefa: Tarefa) => {
    setDoneModalTarefa(tarefa);
  };

  const handleDismiss = async (id: string) => {
    if (!window.confirm('Descartar tarefa?')) return;
    await dismissTarefa(id);
    await reload();
  };

  const handleSnooze = async (id: string) => {
    const until = window.prompt('Adiar até quando? (YYYY-MM-DD)');
    if (!until) return;
    await snoozeTarefa(id, until);
    await reload();
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CheckSquare className="h-6 w-6 text-amber-600" />
          Tarefas
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNewModal(true)}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Nova tarefa
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Atualizando...' : 'Atualizar checks'}
          </button>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex border-b border-gray-200">
        {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              status === s
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {/* Severity filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Severidade:</span>
        {['', 'critical', 'warning', 'info'].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setSeverity(s)}
            className={`px-2 py-1 text-xs rounded ${
              severity === s
                ? 'bg-gray-700 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Todas'}
          </button>
        ))}
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Carregando…</div>
      ) : tarefas.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Inbox className="h-8 w-8 mx-auto mb-2" />
          Nenhuma tarefa {STATUS_LABELS[status].toLowerCase()}.
        </div>
      ) : (
        <div className="space-y-2">
          {tarefas.map((t) => {
            const sev = t.tags.severity || 'info';
            const style = SEVERITY_STYLES[sev] || SEVERITY_STYLES.info;
            const due = t.tags['due-date'];
            const overdue = due && daysDelta(due) > 0 && status === 'open';
            const url = t.tags['action-url'];
            const isComputed = (t.tags.source || '').startsWith('computed:');

            return (
              <div key={t['tarefa-id']} className={`border rounded-lg p-3 bg-white ${style.row}`}>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5">{style.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{t.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${style.chip}`}>{sev}</span>
                      {isComputed && (
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">auto</span>
                      )}
                      {!isComputed && t.tags.source === 'manual' && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">manual</span>
                      )}
                    </div>
                    {t.tags.description && (
                      <pre className="text-sm text-gray-600 mt-1 whitespace-pre-line font-sans">
                        {t.tags.description.replace(/\\n/g, '\n')}
                      </pre>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {due && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {overdue ? (
                            <span className="font-medium text-red-600">
                              Vencido {daysDelta(due)}d ({fmtDate(due)})
                            </span>
                          ) : (
                            <span>Vence {fmtDate(due)}</span>
                          )}
                        </span>
                      )}
                      <span>Criada {fmtTs(t.tags['created-at'])}</span>
                      {t.tags['done-at'] && (
                        <span>Concluída {fmtTs(t.tags['done-at'])} por {t.tags['done-by']}</span>
                      )}
                      {t.tags['resolved-by-txn-ids'] && (
                        <span>
                          Resolvida por:{' '}
                          {t.tags['resolved-by-txn-ids'].split(',').map((tid, i, arr) => (
                            <React.Fragment key={tid}>
                              <Link to={`/lancamentos?id=${tid.trim()}`} className="text-blue-600 hover:underline">
                                {tid.trim().slice(0, 8)}
                              </Link>
                              {i < arr.length - 1 && ', '}
                            </React.Fragment>
                          ))}
                        </span>
                      )}
                      {t.tags['resolved-reason'] && (
                        <span>Motivo: <code className="text-xs">{t.tags['resolved-reason']}</code></span>
                      )}
                      {t.tags['dismissed-at'] && (
                        <span>Descartada {fmtTs(t.tags['dismissed-at'])} por {t.tags['dismissed-by']}</span>
                      )}
                      {t.tags['snoozed-until'] && (
                        <span>Adiada até {fmtDate(t.tags['snoozed-until'])}</span>
                      )}
                    </div>
                  </div>
                  {(status === 'open' || status === 'snoozed') && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {t.tags.doc && (
                        <a
                          href={`/documentos?key=${encodeURIComponent(t.tags.doc)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded hover:bg-purple-100 border border-purple-200"
                          title={t.tags.doc}
                        >
                          📎 Doc
                        </a>
                      )}
                      {url && (
                        <Link
                          to={url}
                          className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 border border-blue-200"
                        >
                          Abrir
                        </Link>
                      )}
                      <button
                        onClick={() => handleDone(t)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        title="Marcar concluída"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      {status === 'open' && (
                        <button
                          onClick={() => handleSnooze(t['tarefa-id'])}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                          title="Adiar"
                        >
                          <Clock className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDismiss(t['tarefa-id'])}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                        title="Descartar"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {doneModalTarefa && (
        <MarkDoneModal
          tarefa={doneModalTarefa}
          onClose={() => setDoneModalTarefa(null)}
          onDone={() => {
            setDoneModalTarefa(null);
            reload();
          }}
        />
      )}

      {showNewModal && (
        <NewTarefaModal
          onClose={() => setShowNewModal(false)}
          onCreated={() => {
            setShowNewModal(false);
            reload();
          }}
        />
      )}
    </div>
  );
}
